import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from './Footer';
import SuccessModal from './SuccessModal';

const JobsPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    description: '',
    resume: null
  });
  
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const handleChange = (e) => {
    if (e.target.name === 'resume') {
      setFormData({
        ...formData,
        [e.target.name]: e.target.files[0]
      });
    } else {
      setFormData({
        ...formData,
        [e.target.name]: e.target.value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('position', formData.position);
      formDataToSend.append('experience', formData.experience);
      formDataToSend.append('description', formData.description);
      
      if (formData.resume) {
        formDataToSend.append('resume', formData.resume);
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/job-application`, {
        method: 'POST',
        body: formDataToSend, // Don't set Content-Type header for FormData
      });

      const result = await response.json();
      
      if (response.ok) {
        setSuccessMessage('Your job application has been submitted successfully! We will review your application and contact you if there are suitable opportunities.');
        setShowSuccessModal(true);
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          position: '',
          experience: '',
          description: '',
          resume: null
        });
        // Reset file input
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = '';
      } else {
        alert(`Error: ${result.detail || 'Failed to submit application. Please try again.'}`);
      }
    } catch (error) {
      console.error('Job application submission error:', error);
      alert('Failed to submit application. Please check your internet connection and try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-emerald-900">
      <div className="container mx-auto px-4 py-8">
        
        {/* Header with Back Button */}
        <div className="mb-8">
          <Link 
            to="/"
            className="inline-flex items-center text-white hover:text-indigo-300 transition-colors duration-200 mb-4"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Jobs & Careers
            </h1>
            <p className="text-xl text-gray-200 max-w-2xl mx-auto">
              Join our team of electrical professionals and build a rewarding career with D.C. Technology Corp.
            </p>
          </div>
        </div>

        {/* Resume Application Form */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 mb-12">
          <h2 className="text-2xl font-semibold text-white mb-6">Submit Your Resume</h2>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Personal Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name */}
              <div>
                <label className="block text-white text-sm font-medium mb-2">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-400/30"
                  placeholder="Enter your full name"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-white text-sm font-medium mb-2">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-400/30"
                  placeholder="Enter your email address"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Phone */}
              <div>
                <label className="block text-white text-sm font-medium mb-2">Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-400/30"
                  placeholder="Enter your phone number"
                />
              </div>

              {/* Position Interested In */}
              <div>
                <label className="block text-white text-sm font-medium mb-2">Position of Interest</label>
                <input
                  type="text"
                  name="position"
                  value={formData.position}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-400/30"
                  placeholder="e.g., Electrician, Project Manager, etc."
                />
              </div>
            </div>

            {/* Experience Level */}
            <div>
              <label className="block text-white text-sm font-medium mb-2">Years of Experience</label>
              <select
                name="experience"
                value={formData.experience}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-400/30"
              >
                <option value="" className="bg-gray-800">Select your experience level</option>
                <option value="entry" className="bg-gray-800">Entry Level (0-2 years)</option>
                <option value="mid" className="bg-gray-800">Mid Level (3-5 years)</option>
                <option value="senior" className="bg-gray-800">Senior Level (6-10 years)</option>
                <option value="expert" className="bg-gray-800">Expert Level (10+ years)</option>
              </select>
            </div>

            {/* Resume Upload */}
            <div>
              <label className="block text-white text-sm font-medium mb-2">Resume Upload</label>
              <div className="relative">
                <input
                  type="file"
                  name="resume"
                  onChange={handleChange}
                  accept=".pdf,.doc,.docx"
                  className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-600 file:text-white hover:file:bg-indigo-700 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-400/30"
                />
              </div>
              <p className="text-gray-400 text-xs mt-2">
                Accepted formats: PDF, DOC, DOCX (Max 10MB)
              </p>
            </div>

            {/* Brief Description */}
            <div>
              <label className="block text-white text-sm font-medium mb-2">Brief Description *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows="4"
                className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-400/30"
                placeholder="Please provide a brief description of your background, skills, and why you're interested in working with D.C. Technology Corp."
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 px-6 rounded-lg font-semibold text-lg transition-all duration-300 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-4 focus:ring-indigo-500/50"
            >
              Submit Application
            </button>
          </form>
        </div>

        {/* Company Benefits */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 mb-8">
          <h2 className="text-3xl font-semibold text-white text-center mb-8">Why Work With Us?</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Comprehensive Benefits</h3>
              <p className="text-gray-300">Health, dental, vision insurance, 401(k) matching, and paid time off.</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Career Growth</h3>
              <p className="text-gray-300">Professional development opportunities and clear advancement paths.</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Team Environment</h3>
              <p className="text-gray-300">Work with experienced professionals in a collaborative environment.</p>
            </div>
          </div>
        </div>

        {/* Application Information */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 text-center">
          <h2 className="text-2xl font-semibold text-white mb-4">
            Application Process
          </h2>
          <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
            All resume submissions are sent directly to <span className="font-semibold text-white">corporate.dccorp@gmail.com</span>. 
            We review all applications and will contact qualified candidates if suitable positions become available.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-indigo-600/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl">📋</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Submit Resume</h3>
              <p className="text-gray-300 text-sm">Upload your resume and tell us about yourself</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl">👀</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">We Review</h3>
              <p className="text-gray-300 text-sm">Our team carefully reviews all applications</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-emerald-600/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl">📞</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">We Connect</h3>
              <p className="text-gray-300 text-sm">Qualified candidates will be contacted directly</p>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
      
      <SuccessModal 
        isVisible={showSuccessModal}
        message={successMessage}
        onClose={() => setShowSuccessModal(false)}
      />
    </div>
  );
};

export default JobsPage;